import { useQuery } from '@tanstack/react-query';

import React, { useState } from 'react';

import { Language, Layers } from '@mui/icons-material';
import { Box, Button, Divider, Paper, Skeleton, Stack, Typography } from '@mui/material';

import { useApiClient, useToastService } from '@@common';
import { CopyToClipboardButton } from '@@components';

import {
	AddServiceConnectionRequest,
	ChangeSetClient,
	ChangeSetViewModel,
	EnvironmentClient,
	EnvironmentModel,
	ServiceConnectionClient,
	ServiceConnectionModel,
	SetEnvironmentAttributesRequest,
} from '../../../api/api';
import SidePanelDetail from '../../../components/Panels/SidePanelDetail';
import ChangeSetStatusChip from '../../ChangeSets/ChangeSetStatusChip';
import AttributesList from './AttributesList';
import ChangeSetSelectionDialog from './ChangeSetSelectionDialog';
import ConfigureDomainNameDialog from './ConfigureDomainNameDialog';
import ServiceConnectionSelectionDialog from '../../ServiceConnections/Components/ServiceConnectionSelectionDialog';
import ServiceConnectionsList from './ServiceConnectionsList';

const ENVIRONMENT_DETAILS_PANEL_STORAGE_KEY = 'environmentDetailsPanel.width';
const ENVIRONMENT_DETAILS_PANEL_DEFAULT_WIDTH = 500;
const ENVIRONMENT_DETAILS_PANEL_MIN_WIDTH = 360;
const ENVIRONMENT_DETAILS_PANEL_MAX_WIDTH = 1000;

interface EnvironmentDetailsPanelProps {
	environment: EnvironmentModel;
	onClose: () => void;
	onRefresh?: () => void;
	top?: number;
}

export default function EnvironmentDetailsPanel({ environment, onClose, onRefresh, top = 0 }: EnvironmentDetailsPanelProps) {
	const client = useApiClient(EnvironmentClient);
	const serviceConnectionClient = useApiClient(ServiceConnectionClient);
	const changeSetClient = useApiClient(ChangeSetClient);
	const toastService = useToastService();
	const [openServiceConnectionDialog, setOpenServiceConnectionDialog] = useState(false);
	const [isUpdatingServiceConnections, setIsUpdatingServiceConnections] = useState(false);
	const [openDomainNameDialog, setOpenDomainNameDialog] = useState(false);
	const [openChangeSetDialog, setOpenChangeSetDialog] = useState(false);
	const [panelWidth, setPanelWidth] = useState(ENVIRONMENT_DETAILS_PANEL_DEFAULT_WIDTH);

	// Collect all service connection IDs (main + associated)
	const allServiceConnectionIds = React.useMemo(() => {
		const ids: string[] = [];
		if (environment.serviceConnectionId) {
			ids.push(environment.serviceConnectionId);
		}
		if (environment.serviceConnections) {
			environment.serviceConnections.forEach((sc) => {
				if (!ids.includes(sc.serviceConnectionId)) {
					ids.push(sc.serviceConnectionId);
				}
			});
		}
		return ids;
	}, [environment]);

	// Load all service connections individually (fallback approach)
	const { data: allServiceConnectionsResult, isLoading: isLoadingServiceConnections } = useQuery({
		queryKey: ['allServiceConnections', allServiceConnectionIds],
		queryFn: async () => {
			if (allServiceConnectionIds.length === 0) return { items: [] };
			try {
				// Load each service connection individually
				var result = await serviceConnectionClient.search({ ids: allServiceConnectionIds });
				return { items: result.items };
			} catch (error) {
				console.warn('Failed to load service connections:', error);
				return { items: [] };
			}
		},
		enabled: allServiceConnectionIds.length > 0,
	});

	// Extract main service connection and associated connections
	const allServiceConnections = React.useMemo(() => allServiceConnectionsResult?.items || [], [allServiceConnectionsResult?.items]);
	const mainServiceConnection = React.useMemo(
		() => (environment.serviceConnectionId ? allServiceConnections.find((sc) => sc.id === environment.serviceConnectionId) : null),
		[environment.serviceConnectionId, allServiceConnections],
	);
	const associatedServiceConnections = React.useMemo(
		() => allServiceConnections.filter((sc) => sc.id !== environment.serviceConnectionId),
		[allServiceConnections, environment.serviceConnectionId],
	);

	// Load associated ChangeSet
	const { data: changeSet, isLoading: isLoadingChangeSet } = useQuery<ChangeSetViewModel | null>({
		queryKey: ['changeSet', environment.changeSetId],
		queryFn: async () => {
			if (!environment.changeSetId) return null;
			try {
				return await changeSetClient.getById(environment.changeSetId);
			} catch (error) {
				console.warn('Failed to load ChangeSet:', error);
				return null;
			}
		},
		enabled: !!environment.changeSetId,
	});

	const handleRefresh = () => {
		if (onRefresh) {
			onRefresh();
		}
	};

	const handleAddServiceConnection = () => {
		setOpenServiceConnectionDialog(true);
	};

	const handleSelectServiceConnection = async (serviceConnection: ServiceConnectionModel) => {
		// Close dialog immediately
		setOpenServiceConnectionDialog(false);
		setIsUpdatingServiceConnections(true);

		try {
			const request: AddServiceConnectionRequest = {
				serviceConnectionId: serviceConnection.id,
				serviceConnectionType: serviceConnection.type,
				serviceConnectionCategory: serviceConnection.category,
			};

			await client.addServiceConnection(environment.id, request);
			toastService.success(`Service connection "${serviceConnection.name}" added successfully.`);

			// Refresh environment details
			if (onRefresh) {
				await onRefresh();
			}
		} catch (error) {
			console.error('Error adding service connection:', error);
			const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
			toastService.error(`Failed to add service connection: ${errorMessage}`);
		} finally {
			setIsUpdatingServiceConnections(false);
		}
	};

	const handleRemoveServiceConnection = async (serviceConnectionId: string) => {
		setIsUpdatingServiceConnections(true);

		try {
			await client.removeServiceConnection(environment.id, serviceConnectionId);
			toastService.success('Service connection removed successfully.');

			// Refresh environment details
			if (onRefresh) {
				await onRefresh();
			} else {
				console.warn('onRefresh callback not provided to EnvironmentDetailsPanel');
			}
		} catch (error) {
			console.error('Error removing service connection:', error);
			const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
			toastService.error(`Failed to remove service connection: ${errorMessage}`);
		} finally {
			setIsUpdatingServiceConnections(false);
		}
	};

	const handleLinkChangeSet = () => {
		setOpenChangeSetDialog(true);
	};

	const handleSelectChangeSet = async (selectedChangeSet: ChangeSetViewModel) => {
		// Close dialog immediately
		setOpenChangeSetDialog(false);

		try {
			await client.setChangeSet(environment.id, selectedChangeSet.id);
			toastService.success(`ChangeSet "${selectedChangeSet.name}" linked successfully.`);

			// Refresh environment details to show the new ChangeSet
			if (onRefresh) {
				await onRefresh();
			} else {
				console.warn('onRefresh callback not provided to EnvironmentDetailsPanel');
			}
		} catch (error) {
			console.error('Error linking ChangeSet:', error);
			const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
			toastService.error(`Failed to link ChangeSet: ${errorMessage}`);
		}
	};

	const handleUnlinkChangeSet = async () => {
		// Close dialog immediately
		setOpenChangeSetDialog(false);

		try {
			await client.setChangeSet(environment.id, undefined);
			toastService.success('ChangeSet unlinked successfully.');

			// Refresh environment details to reflect the removal
			if (onRefresh) {
				await onRefresh();
			} else {
				console.warn('onRefresh callback not provided to EnvironmentDetailsPanel');
			}
		} catch (error) {
			console.error('Error unlinking ChangeSet:', error);
			const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
			toastService.error(`Failed to unlink ChangeSet: ${errorMessage}`);
		}
	};

	const handleConfigureDomainName = () => {
		setOpenDomainNameDialog(true);
	};

	const handleSaveDomainName = async (domainName: string) => {
		try {
			const request: SetEnvironmentAttributesRequest = {
				attributes: {
					DomainName: domainName,
				},
			};

			await client.setAttributes(environment.id, request);
			toastService.success('Domain name configured successfully.');

			// Refresh environment details
			if (onRefresh) {
				await onRefresh();
			}
		} catch (error) {
			console.error('Error setting domain name:', error);
			const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
			toastService.error(`Failed to configure domain name: ${errorMessage}`);
			throw error; // Re-throw to prevent dialog from closing
		}
	};

	const handleDeleteAttributes = async (keys: string[]) => {
		if (keys.length === 0) return;

		try {
			// Delete attributes by setting them to null
			const attributesChanges: { [key: string]: any } = {};
			keys.forEach((key) => {
				attributesChanges[key] = null;
			});

			const request: SetEnvironmentAttributesRequest = {
				attributes: attributesChanges,
			};

			await client.setAttributes(environment.id, request);
			toastService.success(`${keys.length} attribute${keys.length !== 1 ? 's' : ''} removed successfully`);

			// Refresh environment details
			if (onRefresh) {
				await onRefresh();
			}
		} catch (error) {
			console.error('Error deleting attributes:', error);
			const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
			toastService.error(`Failed to delete attributes: ${errorMessage}`);
			throw error;
		}
	};

	const handleSaveCustomAttributes = async (editedAttributes: { [key: string]: string }) => {
		try {
			// Get current custom attributes
			const currentCustomAttrs: { [key: string]: string } = {};
			if (environment.attributes) {
				Object.entries(environment.attributes).forEach(([key, value]) => {
					if (key.startsWith('Custom:')) {
						currentCustomAttrs[key] = String(value);
					}
				});
			}

			// Build attributes object with all changes
			const attributesChanges: { [key: string]: any } = {};

			// Check for new/updated attributes
			Object.entries(editedAttributes).forEach(([key, value]) => {
				if (currentCustomAttrs[key] !== value) {
					attributesChanges[key] = value;
				}
			});

			// Check for deleted attributes (set to null)
			Object.keys(currentCustomAttrs).forEach((key) => {
				if (!(key in editedAttributes)) {
					attributesChanges[key] = null;
				}
			});

			// Execute setAttributes call with all changes in one request
			if (Object.keys(attributesChanges).length > 0) {
				const request: SetEnvironmentAttributesRequest = {
					attributes: attributesChanges,
				};
				await client.setAttributes(environment.id, request);

				const changeCount = Object.keys(attributesChanges).length;
				toastService.success(`Custom attributes updated successfully (${changeCount} change${changeCount !== 1 ? 's' : ''}).`);
			} else {
				toastService.info('No changes to save.');
			}

			// Refresh environment details
			if (onRefresh) {
				await onRefresh();
			}
		} catch (error) {
			console.error('Error saving custom attributes:', error);
			const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
			toastService.error(`Failed to save custom attributes: ${errorMessage}`);
			throw error; // Re-throw to keep edit mode open
		}
	};

	return (
		<>
			<SidePanelDetail
				title="Environment Details"
				isLoading={false}
				isFetching={false}
				error={null}
				position="standalone"
				width={panelWidth}
				top={top}
				onClose={onClose}
				onRefresh={onRefresh ? handleRefresh : undefined}
				resizeStorageKey={ENVIRONMENT_DETAILS_PANEL_STORAGE_KEY}
				defaultWidth={ENVIRONMENT_DETAILS_PANEL_DEFAULT_WIDTH}
				minWidth={ENVIRONMENT_DETAILS_PANEL_MIN_WIDTH}
				maxWidth={ENVIRONMENT_DETAILS_PANEL_MAX_WIDTH}
				setWidth={setPanelWidth}
			>
				{/* Environment Summary */}
				<Box sx={{ mb: 2 }}>
					{/* Environment Name */}
					<Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
						<Typography variant="body2" color="textSecondary">
							Name:
						</Typography>
						<Typography variant="body2" sx={{ flexGrow: 1 }}>
							{environment.name}
						</Typography>
						<CopyToClipboardButton value={environment.name} size="small" iconSize={16} title="Copy name to clipboard" edge={false} sx={{ p: 0.5 }} />
					</Stack>

					{/* Environment Code */}
					<Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
						<Typography variant="body2" color="textSecondary">
							Code:
						</Typography>
						<Typography variant="body2" sx={{ flexGrow: 1 }}>
							{environment.code || 'N/A'}
						</Typography>
						{environment.code && (
							<CopyToClipboardButton value={environment.code} size="small" iconSize={16} title="Copy code to clipboard" edge={false} sx={{ p: 0.5 }} />
						)}
					</Stack>

					{/* Environment ID */}
					<Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
						<Typography variant="body2" color="textSecondary">
							Id:
						</Typography>
						<Typography variant="body2" sx={{ flexGrow: 1 }}>
							{environment.id}
						</Typography>
						<CopyToClipboardButton value={environment.id} size="small" iconSize={16} title="Copy ID to clipboard" edge={false} sx={{ p: 0.5 }} />
					</Stack>

					{/* Tags */}
					<Stack direction="row" spacing={2} alignItems="flex-start" sx={{ mb: 1 }}>
						<Typography variant="body2" color="textSecondary" sx={{ mt: 0.25 }}>
							Tags:
						</Typography>
						<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, flexGrow: 1 }}>
							{environment.tags?.map((tag, index) => (
								<Typography
									key={index}
									variant="caption"
									sx={{
										backgroundColor: 'rgba(25, 118, 210, 0.08)',
										color: '#1976d2',
										border: '1px solid #1976d2',
										borderRadius: '12px',
										px: 1,
										py: 0.25,
										fontSize: '0.7rem',
									}}
								>
									{tag}
								</Typography>
							))}
						</Box>
					</Stack>
				</Box>

				<Divider sx={{ my: 2 }} />

				{/* Domain Name Configuration */}
				<Box sx={{ mb: 3 }}>
					<Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
						<Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
							Domain
						</Typography>
						<Button variant="outlined" size="small" startIcon={<Language />} onClick={handleConfigureDomainName}>
							Set
						</Button>
					</Stack>

					{environment.attributes?.['DomainName'] ? (
						<Paper sx={{ p: 2, bgcolor: 'success.50', border: '1px solid', borderColor: 'success.200' }}>
							<Stack direction="row" spacing={2} alignItems="center">
								<Typography variant="body2" sx={{ fontWeight: 'medium', flexGrow: 1 }}>
									{environment.attributes['DomainName']}
								</Typography>
								<CopyToClipboardButton
									value={environment.attributes['DomainName']}
									size="small"
									iconSize={16}
									title="Copy domain name to clipboard"
									edge={false}
									sx={{ p: 0.5 }}
								/>
							</Stack>
						</Paper>
					) : (
						<Paper sx={{ p: 2, bgcolor: 'grey.50', border: '1px dashed', borderColor: 'grey.300', textAlign: 'center' }}>
							<Typography variant="body2" color="textSecondary">
								No domain name configured
							</Typography>
						</Paper>
					)}
				</Box>

				<Divider sx={{ my: 2 }} />
				{/* Service Connections */}
				<ServiceConnectionsList
					mainServiceConnection={mainServiceConnection}
					serviceConnections={environment.serviceConnections || []}
					serviceConnectionDetails={associatedServiceConnections}
					onLink={handleAddServiceConnection}
					onUnlink={handleRemoveServiceConnection}
					isLoading={isUpdatingServiceConnections}
					isLoadingDetails={isLoadingServiceConnections}
					showMainConnection={!!environment.serviceConnectionId}
				/>

				<Divider sx={{ my: 2 }} />

				{/* ChangeSet */}
				<Box sx={{ mb: 3 }}>
					<Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
						<Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
							Change Set
						</Typography>
						<Button variant="outlined" size="small" startIcon={<Layers />} onClick={handleLinkChangeSet}>
							Set
						</Button>
					</Stack>

					{isLoadingChangeSet ? (
						<Box sx={{ p: 2 }}>
							<Skeleton variant="text" width="60%" height={24} sx={{ mb: 1 }} />
							<Skeleton variant="text" width="40%" height={20} />
						</Box>
					) : changeSet ? (
						<Paper sx={{ p: 2, bgcolor: 'secondary.50', border: '1px solid', borderColor: 'secondary.200' }}>
							<Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
								<Typography variant="body2" sx={{ fontWeight: 'medium', flexGrow: 1 }}>
									{changeSet.name}
								</Typography>
								<ChangeSetStatusChip status={changeSet.status} size="small" />
							</Stack>
						</Paper>
					) : environment.changeSetId ? (
						<Paper sx={{ p: 2, bgcolor: 'grey.50', textAlign: 'center' }}>
							<Typography variant="body2" color="textSecondary">
								Failed to load ChangeSet details
							</Typography>
						</Paper>
					) : (
						<Paper sx={{ p: 2, bgcolor: 'grey.50', border: '1px dashed', borderColor: 'grey.300', textAlign: 'center' }}>
							<Typography variant="body2" color="textSecondary">
								No ChangeSet associated with this environment
							</Typography>
						</Paper>
					)}
				</Box>

				<Divider sx={{ my: 2 }} />

				{/* Attributes */}

				<AttributesList
					attributes={environment.attributes}
					title="Attributes"
					emptyMessage="No attributes defined for this environment"
					onSaveCustomAttributes={handleSaveCustomAttributes}
					onDeleteAttributes={handleDeleteAttributes}
				/>
			</SidePanelDetail>

			{/* Service Connection Selection Dialog */}
			<ServiceConnectionSelectionDialog
				open={openServiceConnectionDialog}
				onClose={() => setOpenServiceConnectionDialog(false)}
				onSelect={handleSelectServiceConnection}
				excludeIds={[
					...(environment.serviceConnectionId ? [environment.serviceConnectionId] : []),
					...(environment.serviceConnections?.map((sc) => sc.serviceConnectionId) || []),
				]}
			/>

			{/* ChangeSet Selection Dialog */}
			<ChangeSetSelectionDialog
				open={openChangeSetDialog}
				onClose={() => setOpenChangeSetDialog(false)}
				onSelect={handleSelectChangeSet}
				onRemove={handleUnlinkChangeSet}
				currentChangeSetId={environment.changeSetId}
				excludeIds={environment.changeSetId ? [environment.changeSetId] : []}
			/>

			{/* Domain Name Configuration Dialog */}
			<ConfigureDomainNameDialog
				open={openDomainNameDialog}
				onClose={() => setOpenDomainNameDialog(false)}
				onSave={handleSaveDomainName}
				currentDomainName={environment.attributes?.['DomainName'] as string | undefined}
				environmentName={environment.name}
			/>
		</>
	);
}
